import { useState, useEffect, useCallback } from 'react';
import { profileApi } from '../api/profile';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import Button from './Button';
import CodeBoxes from './CodeBoxes';
import styles from './Force2faModal.module.css';


/**
 * A blocking modal shown when the user MUST enroll in 2FA before doing
 * anything else. Triggered by user.must_enroll_2fa_now === true.
 *
 * Three states:
 *   - 'ready' — explains why 2FA is being required; button to send the code
 *   - 'verifying' — code entry
 *   - 'sending' — request to /enroll/start in flight
 *
 * There is no cancel button. The only ways out:
 *   - Complete enrollment successfully (which clears must_enroll_2fa_now)
 *   - Sign out (the user can use the header user menu, which is layered above
 *     the modal backdrop; we do NOT hide the header)
 *
 * On success the AuthContext's refresh() is called so must_enroll_2fa_now flips
 * to false and the modal disappears.
 */
export default function Force2faModal() {
  const { user, refresh } = useAuth();
  const { t } = useLanguage();

  // mode: 'ready' | 'sending' | 'verifying'
  const [mode, setMode] = useState('ready');
  const [enrollToken, setEnrollToken] = useState('');
  const [emailMasked, setEmailMasked] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // If user becomes null (signed out) or no longer requires enrollment, the
  // parent stops rendering this component. We reset our local state here so a
  // future re-mount starts clean.
  useEffect(() => {
    return () => {
      setMode('ready');
      setCode('');
      setError(null);
    };
  }, []);

  const handleSendCode = useCallback(async () => {
    setError(null);
    setMode('sending');
    try {
      const result = await profileApi.tfaEnrollStart();
      setEnrollToken(result.enrollment_token);
      setEmailMasked(result.email_masked);
      setCode('');
      setMode('verifying');
    } catch (e) {
      setError(e.message);
      setMode('ready');
    }
  }, []);

  const handleVerify = async (e) => {
    e.preventDefault();
    if (code.length !== 6) return;
    setSubmitting(true);
    setError(null);
    try {
      await profileApi.tfaEnrollVerify(enrollToken, code);
      // Refresh user — must_enroll_2fa_now should flip to false and modal closes
      await refresh();
    } catch (err) {
      setError(err.message);
      setCode('');
    } finally {
      setSubmitting(false);
    }
  };

  const handleResend = async () => {
    setCode('');
    await handleSendCode();
  };

  if (!user || !user.must_enroll_2fa_now) return null;

  return (
    <div className={styles.backdrop} role="dialog" aria-modal="true" aria-labelledby="force2fa-title">
      <div className={styles.modal}>
        <h2 id="force2fa-title" className={styles.title}>
          {t('force2fa.title')}
        </h2>

        <p className={styles.body}>
          {t('force2fa.body')}
        </p>

        {error && <div className={styles.errorBox}>{error}</div>}

        {mode === 'ready' && (
          <>
            <p className={styles.helpText}>
              {t('force2fa.help_ready')}
            </p>
            <Button block large onClick={handleSendCode} loading={false}>
              {t('force2fa.send_code')}
            </Button>
          </>
        )}

        {mode === 'sending' && (
          <p className={styles.helpText} style={{ textAlign: 'center' }}>
            {t('force2fa.sending')}
          </p>
        )}

        {mode === 'verifying' && (
          <form onSubmit={handleVerify}>
            <p className={styles.helpText}>
              {t('force2fa.help_verifying', { email: emailMasked })}
            </p>
            <CodeBoxes value={code} onChange={setCode} disabled={submitting} />
            <div style={{ marginTop: 'var(--space-3)' }}>
              <Button type="submit" block large loading={submitting}
                disabled={code.length !== 6}>
                {t('force2fa.verify')}
              </Button>
            </div>
            <button
              type="button"
              onClick={handleResend}
              className={styles.linkButton}
              disabled={submitting}
            >
              {t('force2fa.resend')}
            </button>
          </form>
        )}

        <p className={styles.footnote}>
          {t('force2fa.footnote')}
        </p>
      </div>
    </div>
  );
}
